import { useState } from 'react'
import { CompanyAPI } from 'apis/CompanyAPI'
import { saveAs } from 'file-saver'
import * as ExcelJS from 'exceljs'
import { isoToDateTimeLocalSeconds, formatDateToLocal } from 'utlis/timeUtils'
import { useToast } from '@chakra-ui/react'



const customFormat = [
    { companies: ['מאניה', 'נויז', 'סאבו'], columns: ['store.code', 'mkt_1', 'amount'] },
    { companies: ['קיווי', 'בוגארט', 'מיננה', 'הוניגמן'], columns: ['upc', 'mkt_1', 'amount']},
    { companies: ["אינטר ג'ינס", 'ריפליי', 'סטיב מאדן'], columns: ['upc', 'mkt_1', 'amount', 'description']},
    { companies: ['דלתא', 'פיקס', 'פנטה ריי', 'ויקטוריה', 'ויקטוריה סיקרט', 'BBW'], columns: [ 'mkt_1', 'amount', 'scanned_at']},
    { companies: ['מקס סטוק', 'מיני מקס', 'מיה אינספריישן'], columns: ['row_name', 'category_name', 'upc', 'mkt_1', 'amount', 'description'] },
    { companies: ['טבע נאות'], columns: ['custom_row_name', 'row_name', 'category_name', 'upc', 'mkt_1', 'amount', 'description'] },
    { companies: ['יונידרס'], columns: ['row_name', 'upc', 'amount'] },
    { companies: [], columns: ['mkt_1', 'amount'] },
]

const companyExportFormats = [
    { companies: ['בילבונג'], format: 'billabongTxt', label: 'TXT', filename: 'count{storeCode}.txt' },
    { companies: ['גלי', 'Gali', 'ניין ווסט', 'אלדו'], format: 'fixedWidthDatMkt', label: 'DAT', filename: 'sfira{storeCode}.dat' },
    { companies: ['לי קופר'], format: 'fixedWidthDatUpc', label: 'DAT', filename: 'sfira{storeCode}.dat' },
    { companies: ['קרייזי ליין', 'CrazyLine', 'הפי למון', 'Happy Lemon'], format: 'prefixedDatMkt', label: 'DAT' },
]

const getCompanyExportConfig = (companyName) => companyExportFormats
    .find(({ companies }) => companies.includes(companyName))

const getCompanyExportFormat = (companyName) => getCompanyExportConfig(companyName)?.format || 'excel'

const padRight = (value, length, padChar = ' ') => String(value ?? '').slice(0, length).padEnd(length, padChar)
const padLeft = (value, length, padChar = ' ') => String(value ?? '').slice(-length).padStart(length, padChar)

const formatDateYYYYMMDD = (dateValue) => {
    const date = new Date(dateValue || Date.now())

    if (Number.isNaN(date.getTime())) {
        return formatDateYYYYMMDD(Date.now())
    }

    return [  
        String(date.getDate()).padStart(2, '0'),
        String(date.getMonth() + 1).padStart(2, '0'),
        date.getFullYear(),
    ].join('')
}

const buildBillabongLine = ({ storeCode, mkt_1, amount }) => [
    ' '.repeat(7),
    padLeft(storeCode, 3, '0'),
    ' ',
    padRight(mkt_1, 15),
    padLeft(amount, 22),
    '000',
].join('')

const buildFixedWidthDatLine = ({ date, storeCode, upc, amount }) => [
    date,
    ' ',
    padLeft(storeCode, 3, '0'),
    ' '.repeat(3),
    padRight(upc, 17),
    padLeft(amount, 10),
].join('')

const formatAmountWithTrailingThousands = (amount) => {
    const integerAmount = String(amount ?? '').split('.')[0]
    return padLeft(`${integerAmount}.000`, 10, '0')
}

const buildPrefixedDatLine = ({ storeCode, mkt, amount }) => [
    '003 ',
    padLeft(storeCode, 6, '0'),
    ' ',
    padRight(mkt, 27),
    formatAmountWithTrailingThousands(amount),
].join('')

const useDownloadCounted = (countingId, counting, group_items) => {
    const [isLoading, setIsLoading] = useState(false)
//    const [rowsMap, setRowsMap] = useState({}) // [rowName: number]: [customRowName: string
    const toast = useToast()
    const companyExportConfig = getCompanyExportConfig(counting?.store?.company?.name)
    const companyExcelConfig = customFormat.find((itm) => itm.companies.includes(counting?.store?.company?.name))
    const downloadFormats = [
        { value: 'excel', label: 'Excel' },
        ...(companyExcelConfig ? [{ value: 'excelCustom', label: 'Excel Custom' }] : []),
        ...(companyExportConfig ? [{ value: 'company', label: companyExportConfig.label }] : []),
    ]

    const fetchData = async (countingId) => {
        const resp = await CompanyAPI.downloadItems(countingId, false,  group_items)
        return [resp.counted_items, resp.rows_map, resp.category_rows, resp.catalog_items, resp.users]
    }

    const columns = [
        { title: 'Store', titleHebrew: 'חנות',  name: 'store.code', default: false },
        ...(counting?.settings?.customRowNames ? [{ title: 'Custom Row Name', titleHebrew: 'שם איתור לקוח', name: 'custom_row_name' }] : []),
        { title: 'Row Name', titleHebrew: 'שם איתור', name: 'row_name' },
        { title: 'Category', titleHebrew: 'קטגוריה', name: 'category_name' },
        { title: 'UPC', titleHebrew: 'ברקוד', name: 'upc', isGroupable: true },
        { title: 'MKT', titleHebrew: 'מק"ט', name: 'mkt_1', isGroupable: true },
        { title: 'Amount', titleHebrew: 'כמות', name: 'amount', isGroupable: true },
        { title: 'Description', titleHebrew: 'תיאור', name: 'description', isGroupable: true },
        { title: 'Device Number', titleHebrew: 'מספר מכשיר', name: 'device_number' },
        { title: 'Name', titleHebrew: 'שם', name: 'user_id' },
        { title: 'Length', titleHebrew: 'אורך', name: 'mkt_1_length', isGroupable: true },
        { title: 'Time', titleHebrew: 'תאריך', name: 'scanned_at' },
        { title: 'Invalid', titleHebrew: 'לא חוקי', name: 'is_found' },
        { title: 'Manual', titleHebrew: 'ידני', name: 'is_manual' }
    ] 
 
    const generateExcel = async (filteredCountedItems, rowsMap, categoryRows, catalogItems, users, exportFormat) => {
        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet('Data')
        const limitedColumns = columns
            .filter(v => (!group_items || v?.isGroupable))
            .filter(c => exportFormat !== 'excelCustom'
                || customFormat.find((itm) => itm.companies.includes(counting.store.company.name))?.columns.includes(c.name)
            )
            .map(c => c.default === false ? undefined : c.name)
  

        worksheet.columns = columns
            .filter(header => limitedColumns.includes(header.name))
            .map((header) => ({
                header: header.titleHebrew,
                key: header.name,
                width: ['upc', 'mkt_1', 'scanned_at'].includes(header.name) ? 17 : 10
            }))

        // Precompute lookups to avoid repeated object access in the loop
        const customRowNamesEnabled = !!counting?.settings?.customRowNames
        
        // Use a single array to collect all rows, then add them in bulk
        const rows = filteredCountedItems.map(({ row_name, user_id, upc, mkt_1, amount, device_number, mkt_1_length, scanned_at, is_found, is_manual }) => {
            const rowMap = rowsMap[row_name ?? ''] || {}
           
            return [
                limitedColumns.includes('store.code') ? counting.store.code : undefined,
                ...(customRowNamesEnabled ? [limitedColumns.includes('custom_row_name') ? (rowMap.custom_row_name ?? '') : undefined] : []),
                limitedColumns.includes('row_name') ? (!isNaN(Number(row_name)) ? Number(row_name) : row_name) : undefined,
                limitedColumns.includes('category_name') ? (rowMap.category_row_id ? (categoryRows[rowMap.category_row_id]?.name || '') : '') : undefined,
                limitedColumns.includes('upc') ? upc : undefined,
                limitedColumns.includes('mkt_1') ? mkt_1 : undefined,
                limitedColumns.includes('amount') ? (isNaN(Number(amount)) ? amount : Number(amount)) : undefined,
                limitedColumns.includes('description') ? (catalogItems[upc]?.mkt_2 || '') : undefined,
                limitedColumns.includes('device_number') ? (isNaN(Number(device_number)) ? device_number : Number(device_number)) : undefined,
                limitedColumns.includes('user_id') ? (user_id ? `${users[user_id]?.name ?? ''} ${users[user_id]?.last_name ?? ''}`.trim() : '') : undefined,
                limitedColumns.includes('mkt_1_length') ? (isNaN(Number(mkt_1_length)) ? mkt_1_length : Number(mkt_1_length)) : undefined,
                limitedColumns.includes('scanned_at') ? (exportFormat === 'excelCustom' ? formatDateToLocal(scanned_at) : isoToDateTimeLocalSeconds(scanned_at)) : undefined,
                limitedColumns.includes('is_found') ? (is_found ? 'No' : 'Yes') : undefined,
                limitedColumns.includes('is_manual') ? (is_manual ? 'Yes' : 'No') : undefined
            ].filter(cell => cell !== undefined) // Remove undefined values for columns that are not included
        })

        worksheet.addRows(rows)
        worksheet.views = [{ state: 'frozen', ySplit: 1 }]
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, (exportFormat === 'excel' ? 'Counting_' :'') + `${counting.store.name}_${counting.store.code}.xlsx`)
    }

    const generateBillabongTxt = (filteredCountedItems) => {
        const lines = filteredCountedItems.map(({ mkt_1, amount }) => buildBillabongLine({
            storeCode: counting.store.code,
            mkt_1,
            amount,
        }))
        const blob = new Blob([lines.join('\r\n')], { type: 'text/plain;charset=utf-8' })
        const filename = `count${counting.store.code}.txt` //`${counting.store.name}_${counting.store.code}.txt`
        saveAs(blob, filename)
    }

    const generateFixedWidthDat = (filteredCountedItems, barcodeField) => {
        const date = formatDateYYYYMMDD(counting.date_start || counting.created_at)
        const lines = filteredCountedItems.map((item) => buildFixedWidthDatLine({
            date,
            storeCode: counting.store.code,
            upc: item[barcodeField],
            amount: item.amount,
        }))
        const blob = new Blob([lines.join('\r\n')], { type: 'application/octet-stream;charset=utf-8' })
        const filename = `sfira${counting.store.code}.dat` //`${counting.store.name}_${counting.store.code}.dat`
        saveAs(blob, filename)
    }

    const generatePrefixedDat = (filteredCountedItems) => {
        const lines = filteredCountedItems.map(({ mkt_1, amount }) => buildPrefixedDatLine({
            storeCode: counting.store.code,
            mkt: mkt_1,
            amount,
        }))
        const blob = new Blob([lines.join('\r\n')], { type: 'application/octet-stream;charset=utf-8' })
        // saveAs(blob, `CountingData_${counting.store.company.name}_${counting.store.name}_${counting.store.code}.dat`)
        saveAs(blob, `${counting.store.name}_${counting.store.code}.dat`)
    }

    const exportCounted = async (countedItems, rowsMap, categoryRows, catalogItems, users, selectedFormat = 'company') => {
        const exportFormat = selectedFormat === 'company'
            ? getCompanyExportFormat(counting.store.company.name)
            : selectedFormat

        switch (exportFormat) {
            case 'billabongTxt':
                generateBillabongTxt(countedItems)
                return
            case 'fixedWidthDatMkt':
                generateFixedWidthDat(countedItems, 'mkt_1')
                return
            case 'fixedWidthDatUpc':
                generateFixedWidthDat(countedItems, 'upc')
                return
            case 'prefixedDatMkt':
                generatePrefixedDat(countedItems)
                return
            case 'excel':
            case 'excelCustom':
            default:
                await generateExcel(countedItems, rowsMap, categoryRows, catalogItems, users, exportFormat)
        }
    }

    const downloadCounted = async (selectedFormat = 'company') => {
        setIsLoading(true)
        try {
            const [countedItems, rowsMap, categoryRows, catalogItems, users ] = await fetchData(countingId)
            //const filteredCountedItems = prepareData(countedItems)
            await exportCounted(countedItems, rowsMap, categoryRows, catalogItems, users, selectedFormat)
            toast({
                title: 'Downloading.',
                description: 'The data is being downloaded.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            })
        } catch (error) {
            toast({
                title: 'An error occurred.',
                description: 'Unable to download the data.' + error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            })
        } finally {
            setIsLoading(false)
        }
    }

    return {
        downloadCounted,
        downloadFormats,
        companyExportFormats,
        isLoading,
    }
}

export default useDownloadCounted
